import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_BASE_URL } from '../config/api';
import { Store, Lock, Search, Package, Briefcase, MapPin, Phone, Plus, Trash2, Send, Inbox, ImagePlus, Eye, Star } from 'lucide-react';
// Store icon used in seller account card

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-700',
    new: 'bg-blue-100 text-blue-800',
    converted: 'bg-purple-100 text-purple-800'
  };
  return <Badge className={`${map[status] || 'bg-gray-100 text-gray-700'} text-[10px] capitalize`}>{status}</Badge>;
};

const Stars = ({ avg, count, size = 'text-xs' }) => (
  <span className={`inline-flex items-center gap-1 ${size} font-medium`}>
    <span className="text-amber-500">{'★'.repeat(Math.round(avg))}{'☆'.repeat(5 - Math.round(avg))}</span>
    <span className="text-gray-500">{avg}{count != null ? ` (${count})` : ''}</span>
  </span>
);

const ListingCard = ({ l, onEnquire, mine, onDelete, onOpen }) => (
  <Card className="overflow-hidden hover:shadow-xl transition-shadow flex flex-col cursor-pointer" data-testid={`listing-card-${l.id}`}
    onClick={() => onOpen && onOpen(l)}>
    {l.image_url ? (
      <img src={l.image_url} alt={l.title} className="h-40 w-full object-cover" />
    ) : (
      <div className="h-40 w-full bg-[#1E3A5F]/5 flex items-center justify-center">
        {l.type === 'product' ? <Package className="h-10 w-10 text-[#1E3A5F]/30" /> : <Briefcase className="h-10 w-10 text-[#1E3A5F]/30" />}
      </div>
    )}
    <div className="p-4 flex-1 flex flex-col">
      <div className="flex items-center gap-2 mb-1.5">
        <Badge className={l.type === 'product' ? 'bg-[#E87722]/10 text-[#E87722] text-[10px]' : 'bg-[#1E3A5F]/10 text-[#1E3A5F] text-[10px]'}>
          {l.type === 'product' ? 'Product' : 'Service'}
        </Badge>
        <span className="text-xs text-gray-500">{l.category}</span>
        {mine && <StatusBadge status={l.status} />}
      </div>
      <h3 className="font-bold text-[#1E3A5F] leading-snug">{l.title}</h3>
      <p className="text-sm text-gray-600 mt-1 line-clamp-3 flex-1">{l.description}</p>
      <div className="mt-3 space-y-1">
        {l.price_display && <p className="text-sm font-semibold text-[#E87722]">{l.price_display}</p>}
        <p className="text-xs text-gray-500">
          By {l.seller_id ? (
            <a href={`/marketplace/seller/${l.seller_id}`} onClick={(e) => e.stopPropagation()}
              className="underline hover:text-[#E87722]" data-testid={`seller-link-${l.id}`}>{l.seller_org || l.seller_name}</a>
          ) : (l.seller_org || l.seller_name)}
        </p>
        {l.seller_rating && <div data-testid={`seller-rating-${l.id}`}><Stars avg={l.seller_rating.avg} count={l.seller_rating.count} /></div>}
        {l.location && <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{l.location}</p>}
        {l.contact_phone && !mine && <p className="text-xs text-gray-500 flex items-center gap-1"><Phone className="h-3 w-3" />{l.contact_phone}</p>}
        {mine && (
          <p className="text-xs text-gray-500 flex items-center gap-3 pt-1" data-testid={`listing-analytics-${l.id}`}>
            <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{l.views || 0} view{(l.views || 0) === 1 ? '' : 's'}</span>
            <span className="inline-flex items-center gap-1"><Inbox className="h-3 w-3" />{l.enquiry_count || 0} enquir{(l.enquiry_count || 0) === 1 ? 'y' : 'ies'}</span>
          </p>
        )}
      </div>
      {mine ? (
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onDelete(l.id); }}
          className="mt-3 border-red-300 text-red-600 hover:bg-red-50" data-testid={`delete-listing-${l.id}`}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />Remove
        </Button>
      ) : (
        <Button size="sm" onClick={(e) => { e.stopPropagation(); onEnquire(l); }} className="mt-3 bg-[#E87722] hover:bg-[#d66a1a]" data-testid={`enquire-btn-${l.id}`}>
          <Send className="h-3.5 w-3.5 mr-1.5" />Enquire
        </Button>
      )}
    </div>
  </Card>
);

export default function MarketPlaceHub() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [memberStatus, setMemberStatus] = useState(null); // null=loading
  const [categories, setCategories] = useState({ product: [], service: [] });
  const [listings, setListings] = useState([]);
  const [mine, setMine] = useState([]);
  const [enquiries, setEnquiries] = useState({ sent: [], received: [] });
  const [typeFilter, setTypeFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [q, setQ] = useState('');
  const [enquireFor, setEnquireFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [rateFor, setRateFor] = useState(null);
  const [rateStars, setRateStars] = useState(0);
  const [rateComment, setRateComment] = useState('');
  const [enquiryMsg, setEnquiryMsg] = useState('');
  const [enquiryPhone, setEnquiryPhone] = useState('');
  const [postOpen, setPostOpen] = useState(false);
  const [form, setForm] = useState({ type: 'product', title: '', description: '', category: '', price_display: '', location: '', contact_phone: '', image_url: '' });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sellerStatus, setSellerStatus] = useState(null);
  const [plans, setPlans] = useState(null);
  const [plansOpen, setPlansOpen] = useState(false);
  const [payingPlan, setPayingPlan] = useState(null);
  const [upgradeOptions, setUpgradeOptions] = useState([]);
  const [rzpLoaded, setRzpLoaded] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRzpLoaded(true);
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setMemberStatus({ is_member: false, logged_in: false }); return; }
    axios.get(`${API_BASE_URL}/api/events/user/member-status`, { headers: authHeaders() })
      .then(({ data }) => setMemberStatus(data))
      .catch(() => setMemberStatus({ is_member: false, logged_in: true }));
  }, [user, authLoading]);

  useEffect(() => {
    if (!memberStatus?.is_member) return;
    axios.get(`${API_BASE_URL}/api/market/categories`).then(({ data }) => setCategories(data)).catch(() => {});
    axios.get(`${API_BASE_URL}/api/market/seller/status`, { headers: authHeaders() }).then(({ data }) => setSellerStatus(data)).catch(() => {});
    axios.get(`${API_BASE_URL}/api/market/seller-plans`, { headers: authHeaders() }).then(({ data }) => setPlans(data)).catch(() => {});
    fetchListings();
    fetchMine();
    fetchEnquiries();
  }, [memberStatus]);

  useEffect(() => {
    if (memberStatus?.is_member) fetchListings();
  }, [typeFilter, catFilter]);

  const fetchListings = async () => {
    try {
      const params = {};
      if (typeFilter !== 'all') params.type = typeFilter;
      if (catFilter !== 'all') params.category = catFilter;
      if (q.trim()) params.q = q.trim();
      const { data } = await axios.get(`${API_BASE_URL}/api/market/listings`, { params, headers: authHeaders() });
      setListings(data.listings || []);
    } catch (e) { console.error(e); }
  };

  const fetchMine = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/market/listings/mine`, { headers: authHeaders() });
      setMine(data.listings || []);
    } catch (e) { console.error(e); }
  };

  const fetchEnquiries = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/market/enquiries/mine`, { headers: authHeaders() });
      setEnquiries({ sent: data.sent || [], received: data.received || [] });
    } catch (e) { console.error(e); }
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_BASE_URL}/api/market/upload-image`, fd, { headers: authHeaders() });
      setForm(prev => ({ ...prev, image_url: data.url }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const refreshSellerStatus = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/market/seller/status`, { headers: authHeaders() });
      setSellerStatus(data);
      return data;
    } catch (e) { return null; }
  };

  const openPost = () => {
    if (sellerStatus?.active) setPostOpen(true);
    else setPlansOpen(true);
  };

  const openUpgrade = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/market/seller/upgrade-options`, { headers: authHeaders() });
      setUpgradeOptions(data.options || []);
    } catch (e) { setUpgradeOptions([]); }
    setPlansOpen(true);
  };

  const payForPlan = async (planKey) => {
    if (!rzpLoaded || !window.Razorpay) { alert('Payment gateway is still loading, please try again in a moment.'); return; }
    setPayingPlan(planKey);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/market/seller/create-order`, { plan: planKey }, { headers: authHeaders() });
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        order_id: data.id,
        name: 'CSED Market Place',
        description: `Seller Account — ${plans?.plans?.[planKey]?.name || planKey}`,
        handler: async (resp) => {
          try {
            const v = await axios.post(`${API_BASE_URL}/api/market/seller/verify-payment`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature
            }, { headers: authHeaders() });
            alert(`Your Seller Account is active until ${String(v.data.expires_at).slice(0, 10)}. You can now post listings!`);
            setPlansOpen(false);
            await refreshSellerStatus();
            setPostOpen(true);
          } catch (e) {
            alert(e.response?.data?.detail || 'Payment verification failed. Please contact CSED.');
          }
        },
        theme: { color: '#E87722' }
      };
      new window.Razorpay(options).open();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to start payment');
    } finally {
      setPayingPlan(null);
    }
  };

  const submitListing = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/market/listings`, form, { headers: authHeaders() });
      alert(data.message);
      setPostOpen(false);
      setForm({ type: 'product', title: '', description: '', category: '', price_display: '', location: '', contact_phone: '', image_url: '' });
      fetchMine();
      refreshSellerStatus();
    } catch (e) {
      if (e.response?.status === 402) {
        setPostOpen(false);
        setPlansOpen(true);
      } else {
        alert(e.response?.data?.detail || 'Failed to submit listing');
      }
    } finally {
      setBusy(false);
    }
  };

  const deleteListing = async (id) => {
    if (!window.confirm('Remove this listing?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/market/listings/${id}`, { headers: authHeaders() });
      fetchMine();
      fetchListings();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to remove');
    }
  };

  const openDetail = (l) => {
    setDetailFor(l);
    axios.post(`${API_BASE_URL}/api/market/listings/${l.id}/view`, {}, { headers: authHeaders() }).catch(() => {});
  };

  const submitRating = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE_URL}/api/market/enquiries/${rateFor.id}/rate`,
        { rating: rateStars, comment: rateComment }, { headers: authHeaders() });
      alert('Thank you! Your rating has been recorded.');
      setRateFor(null);
      setRateStars(0);
      setRateComment('');
      fetchEnquiries();
      fetchListings();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setBusy(false);
    }
  };

  const sendEnquiry = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/market/listings/${enquireFor.id}/enquire`,
        { message: enquiryMsg, contact_phone: enquiryPhone }, { headers: authHeaders() });
      alert(data.message);
      setEnquireFor(null);
      setEnquiryMsg('');
      setEnquiryPhone('');
      fetchEnquiries();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send enquiry');
    } finally {
      setBusy(false);
    }
  };

  // ---------- Locked / loading states ----------
  if (authLoading || (user && memberStatus === null)) {
    return (
      <div className="min-h-screen"><Header />
        <div className="pt-40 text-center text-gray-500">Loading Market Place...</div>
        <Footer /></div>
    );
  }

  if (!memberStatus?.is_member) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <section className="bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F]/90 text-white py-20 pt-32 text-center">
          <Store className="h-12 w-12 mx-auto text-[#E87722] mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">A Marketplace Within a Community</h1>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto px-4">
            Connect. Discover. Buy. Sell. Grow — within the CSED community.
          </p>
        </section>
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <p className="text-lg text-gray-700 leading-relaxed" data-testid="market-public-intro">
              The CSED Marketplace is a dedicated platform that brings together producers, service providers and consumers who are part of the CSED community. Unlike conventional marketplaces that connect anonymous sellers and buyers, the CSED Marketplace is built around a <strong>trusted membership ecosystem</strong>. Every participant is connected to the larger CSED community and the Marketplace is endorsed by CSED.
            </p>

            <h2 className="text-2xl font-bold text-[#1E3A5F] mt-12 mb-2">One Community. Many Possibilities.</h2>
            <p className="text-gray-600 mb-6">Whether you are a producer looking for new markets, a service provider looking for customers, or a consumer looking for reliable products and services — the CSED Marketplace creates a common space where members discover and connect with one another.</p>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                ['For Producers & Businesses', 'Showcase your products, reach fellow members and explore new markets within the CSED network.', Package],
                ['For Service Providers', 'Make your professional services visible to organisations, enterprises and individuals within the community.', Briefcase],
                ['For Consumers', 'Discover products and services offered by fellow CSED members — support and transact within the community.', Search]
              ].map(([title, desc, Icon]) => (
                <Card key={title} className="p-6">
                  <Icon className="h-8 w-8 text-[#E87722] mb-3" />
                  <h3 className="font-bold text-[#1E3A5F] mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                </Card>
              ))}
            </div>

            <Card className="p-6 mt-10 bg-[#1E3A5F]/5 border-[#1E3A5F]/10">
              <h2 className="text-xl font-bold text-[#1E3A5F] mb-2">More Than a Marketplace</h2>
              <p className="text-gray-700 text-sm leading-relaxed">
                The CSED Marketplace is part of a larger ecosystem designed to create economic opportunities within the community. Membership opens the door to a network that extends beyond buying and selling — connecting members to opportunities, knowledge, partnerships, resources and other members of the CSED ecosystem.
              </p>
            </Card>

            <h2 className="text-2xl font-bold text-[#1E3A5F] mt-12 mb-6">Why CSED Marketplace?</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                ['CSED-Endorsed', 'A marketplace supported by the credibility and ecosystem of CSED.'],
                ['Member-Based', 'Access is reserved for registered CSED members, creating a defined community of participants.'],
                ['Community-Driven', 'Encourages members to discover, support and do business with one another.'],
                ['Opportunity-Focused', 'Designed to create visibility, market access and new connections for producers and service providers.'],
                ['Growing Ecosystem', 'As the CSED membership grows, so does the potential marketplace for every member.']
              ].map(([title, desc]) => (
                <div key={title} className="bg-white border rounded-lg p-5">
                  <p className="font-semibold text-[#1E3A5F] flex items-center gap-2"><span className="text-[#E87722]">•</span>{title}</p>
                  <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            <Card className="mt-14 p-10 text-center bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F]/90 text-white" data-testid="market-locked-card">
              <Lock className="h-8 w-8 mx-auto text-[#E87722] mb-4" />
              <h2 className="text-2xl font-bold mb-3">Be Part of the Marketplace</h2>
              <p className="text-gray-200 text-sm max-w-2xl mx-auto mb-6">
                The Marketplace is available exclusively to registered CSED members. If you would like your products or services to be discovered by the CSED community — or access to products and services offered by fellow members — become a CSED member and join the Marketplace.
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                {!memberStatus?.logged_in && (
                  <Button variant="outline" onClick={() => navigate('/login')}
                    className="border-white/50 text-white hover:bg-white/10 bg-transparent" data-testid="market-login-btn">Member Login</Button>
                )}
                <Button className="bg-[#E87722] hover:bg-[#d66a1a]" onClick={() => navigate('/membership')} data-testid="market-become-member-btn">
                  Become a CSED Member
                </Button>
              </div>
              <p className="text-xs text-gray-300 mt-5">Your membership is your gateway to the CSED community and its growing marketplace of opportunities.</p>
            </Card>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const catOptions = typeFilter === 'service' ? categories.service : typeFilter === 'product' ? categories.product : Array.from(new Set([...categories.product, ...categories.service]));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F]/90 text-white py-16 pt-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-3" data-testid="market-page-title">Market Place</h1>
          <p className="text-gray-200 max-w-3xl">
            Buy & sell within the trusted CSED member community — products made by members and professional services (legal, audit, consulting and more).
          </p>
        </div>
      </section>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Tabs defaultValue="browse">
          <TabsList className="mb-6">
            <TabsTrigger value="browse" data-testid="tab-browse">Browse</TabsTrigger>
            <TabsTrigger value="mine" data-testid="tab-my-listings">My Listings ({mine.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries ({enquiries.sent.length + enquiries.received.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="browse">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex rounded-lg border overflow-hidden">
                {[['all', 'All'], ['product', 'Products'], ['service', 'Services']].map(([v, label]) => (
                  <button key={v} onClick={() => { setTypeFilter(v); setCatFilter('all'); }}
                    className={`px-4 py-2 text-sm font-medium ${typeFilter === v ? 'bg-[#1E3A5F] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    data-testid={`filter-${v}`}>
                    {label}
                  </button>
                ))}
              </div>
              <Select value={catFilter} onValueChange={setCatFilter}>
                <SelectTrigger className="w-56 bg-white" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {catOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                <Input placeholder="Search listings, sellers..." value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchListings()} className="bg-white" data-testid="market-search-input" />
                <Button variant="outline" onClick={fetchListings} data-testid="market-search-btn"><Search className="h-4 w-4" /></Button>
              </div>
              <Button className="bg-[#E87722] hover:bg-[#d66a1a]" onClick={openPost} data-testid="post-listing-btn">
                <Plus className="h-4 w-4 mr-1.5" />Post a Listing
              </Button>
            </div>

            {listings.length ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {listings.map(l => <ListingCard key={l.id} l={l} onEnquire={setEnquireFor} onOpen={openDetail} />)}
              </div>
            ) : (
              <Card className="p-10 text-center text-gray-500" data-testid="no-listings-message">
                No listings yet{typeFilter !== 'all' || catFilter !== 'all' || q ? ' for this filter' : ''}. Be the first — post your product or service!
              </Card>
            )}
          </TabsContent>

          <TabsContent value="mine">
            {sellerStatus?.has_account && sellerStatus.plan !== 'admin' && (
              <Card className="p-4 mb-5 flex flex-wrap items-center justify-between gap-3" data-testid="seller-account-card">
                <div>
                  <p className="font-bold text-[#1E3A5F]">
                    Seller Account — {sellerStatus.plan_name}
                    <Badge className={`ml-2 text-[10px] ${sellerStatus.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
                      {sellerStatus.active ? 'Active' : 'Expired'}
                    </Badge>
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {sellerStatus.listings_used}/{sellerStatus.listings_allowed == null ? '∞' : sellerStatus.listings_allowed} listings used
                    {sellerStatus.expires_at ? ` · ${sellerStatus.active ? 'renews' : 'expired'} ${String(sellerStatus.expires_at).slice(0, 10)}` : ''}
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {sellerStatus.seller_id && (
                    <Button variant="outline" onClick={() => navigate(`/marketplace/seller/${sellerStatus.seller_id}`)} data-testid="my-storefront-btn">
                      <Store className="h-4 w-4 mr-1.5" />My Storefront
                    </Button>
                  )}
                  {sellerStatus.active && sellerStatus.plan !== 'ultra' && (
                    <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]" onClick={openUpgrade} data-testid="upgrade-plan-btn">
                      Upgrade Plan
                    </Button>
                  )}
                  {(sellerStatus.renewal_due || !sellerStatus.active) && (
                    <Button className="bg-[#E87722] hover:bg-[#d66a1a]" onClick={() => payForPlan(sellerStatus.plan)}
                      disabled={payingPlan === sellerStatus.plan} data-testid="renew-seller-btn">
                      Renew — ₹{Number(sellerStatus.annual_fee || 0).toLocaleString('en-IN')}/yr
                    </Button>
                  )}
                </div>
              </Card>
            )}
            {mine.length ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {mine.map(l => <ListingCard key={l.id} l={l} mine onDelete={deleteListing} />)}
              </div>
            ) : (
              <Card className="p-10 text-center text-gray-500">You haven't posted any listings yet.
                <div className="mt-4"><Button className="bg-[#E87722] hover:bg-[#d66a1a]" onClick={openPost}>Post your first listing</Button></div>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="enquiries">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-[#1E3A5F] mb-3 flex items-center gap-2"><Inbox className="h-4 w-4 text-[#E87722]" />Received on my listings</h3>
                {enquiries.received.length ? enquiries.received.map(e => (
                  <Card key={e.id} className="p-4 mb-3" data-testid={`enquiry-received-${e.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-semibold text-sm text-[#1E3A5F]">{e.listing_title}</p>
                      <StatusBadge status={e.status} />
                    </div>
                    <p className="text-sm text-gray-700">{e.message}</p>
                    <p className="text-xs text-gray-500 mt-1.5">From {e.buyer_name} · {e.buyer_email}{e.buyer_phone ? ` · ${e.buyer_phone}` : ''}</p>
                  </Card>
                )) : <p className="text-sm text-gray-500">No enquiries received yet.</p>}
              </div>
              <div>
                <h3 className="font-bold text-[#1E3A5F] mb-3 flex items-center gap-2"><Send className="h-4 w-4 text-[#E87722]" />Sent by me</h3>
                {enquiries.sent.length ? enquiries.sent.map(e => (
                  <Card key={e.id} className="p-4 mb-3" data-testid={`enquiry-sent-${e.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-semibold text-sm text-[#1E3A5F]">{e.listing_title}</p>
                      <StatusBadge status={e.status} />
                    </div>
                    <p className="text-sm text-gray-700">{e.message}</p>
                    <p className="text-xs text-gray-500 mt-1.5">To {e.seller_name}</p>
                    {e.rating ? (
                      <div className="mt-2" data-testid={`my-rating-${e.id}`}>
                        <span className="text-xs text-gray-500 mr-2">Your rating:</span>
                        <Stars avg={e.rating} />
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="mt-2 border-amber-400 text-amber-600 hover:bg-amber-50"
                        onClick={() => { setRateFor(e); setRateStars(0); setRateComment(''); }} data-testid={`rate-deal-btn-${e.id}`}>
                        <Star className="h-3.5 w-3.5 mr-1.5" />Rate seller
                      </Button>
                    )}
                  </Card>
                )) : <p className="text-sm text-gray-500">You haven't sent any enquiries yet.</p>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Seller plans dialog */}
      <Dialog open={plansOpen} onOpenChange={setPlansOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="seller-plans-dialog">
          <DialogHeader>
            <DialogTitle>{sellerStatus?.active ? 'Upgrade your Seller Plan' : sellerStatus?.has_account ? 'Renew your Seller Account' : 'Become a Market Place Seller'}</DialogTitle>
            <DialogDescription>
              {sellerStatus?.active
                ? 'Pay only the pro-rata difference for the rest of your current year — your renewal date stays the same.'
                : 'Listing on the Market Place requires a Seller Account — a one-time setup fee plus an annual rental. Buyers pay nothing.'}
            </DialogDescription>
          </DialogHeader>
          {sellerStatus?.active && upgradeOptions.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {upgradeOptions.map(o => (
                <Card key={o.plan} className="p-5 flex flex-col" data-testid={`upgrade-plan-${o.plan}`}>
                  <p className="font-bold text-[#1E3A5F] text-lg">{o.name}</p>
                  <p className="text-sm text-gray-600 mt-1">{o.listings == null ? 'Unlimited listings' : `${o.listings} listings`}</p>
                  <div className="mt-3 text-sm text-gray-600 flex-1 space-y-1">
                    <p>Pro-rata annual difference ({o.remaining_days} days left): <span className="font-semibold">₹{o.annual_component.toLocaleString('en-IN')}</span></p>
                    <p>Setup difference: <span className="font-semibold">{o.setup_component ? `₹${o.setup_component.toLocaleString('en-IN')}` : 'FREE'}</span></p>
                  </div>
                  <Button className="mt-4 bg-[#1E3A5F] hover:bg-[#16304f] text-white" onClick={() => payForPlan(o.plan)}
                    disabled={payingPlan === o.plan} data-testid={`pay-upgrade-${o.plan}`}>
                    {payingPlan === o.plan ? 'Opening payment...' : `Upgrade — ₹${o.amount.toLocaleString('en-IN')}`}
                  </Button>
                </Card>
              ))}
            </div>
          ) : (
            <>
              {plans?.launch_offer?.active && !sellerStatus?.has_account && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2.5 text-sm text-green-800 font-medium" data-testid="launch-offer-banner">
              🎉 Launch offer: setup fee waived for the first {plans.launch_offer.limit} sellers — {plans.launch_offer.remaining} spot{plans.launch_offer.remaining === 1 ? '' : 's'} left!
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            {plans && Object.entries(plans.plans).map(([key, p]) => {
              const waived = plans.launch_offer?.active && !sellerStatus?.has_account;
              const renewing = !!sellerStatus?.has_account;
              const total = renewing ? p.annual_fee : (waived ? 0 : p.setup_fee) + p.annual_fee;
              return (
                <Card key={key} className={`p-5 flex flex-col ${key === 'growth' ? 'border-[#E87722] border-2' : ''}`} data-testid={`seller-plan-${key}`}>
                  {key === 'growth' && <Badge className="bg-[#E87722] text-white w-fit mb-2 text-[10px]">Popular</Badge>}
                  <p className="font-bold text-[#1E3A5F] text-lg">{p.name}</p>
                  <p className="text-sm text-gray-600 mt-1">{p.listings == null ? 'Unlimited listings' : `${p.listings} listing${p.listings === 1 ? '' : 's'}`}{key === 'ultra' ? ' + featured in weekly digest' : ''}</p>
                  <div className="mt-3 space-y-1 text-sm flex-1">
                    {!renewing && (
                      <p className="text-gray-600">Setup (one-time):{' '}
                        {waived ? (
                          <><span className="line-through text-gray-400">₹{p.setup_fee.toLocaleString('en-IN')}</span> <span className="text-green-600 font-bold">FREE</span></>
                        ) : (
                          <span className="font-semibold">₹{p.setup_fee.toLocaleString('en-IN')}</span>
                        )}
                      </p>
                    )}
                    <p className="text-gray-600">Annual rental: <span className="font-semibold">₹{p.annual_fee.toLocaleString('en-IN')}/yr</span></p>
                  </div>
                  <Button className="mt-4 bg-[#1E3A5F] hover:bg-[#16304f] text-white" onClick={() => payForPlan(key)}
                    disabled={payingPlan === key} data-testid={`pay-plan-${key}`}>
                    {payingPlan === key ? 'Opening payment...' : `Pay ₹${total.toLocaleString('en-IN')} ${renewing ? '(1 year)' : '& Start Selling'}`}
                  </Button>
                </Card>
              );
            })}
          </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Listing detail dialog */}
      <Dialog open={!!detailFor} onOpenChange={(o) => !o && setDetailFor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="listing-detail-dialog">
          {detailFor && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#1E3A5F]">{detailFor.title}</DialogTitle>
                <DialogDescription>
                  {detailFor.type === 'product' ? 'Product' : 'Professional Service'} · {detailFor.category}
                </DialogDescription>
              </DialogHeader>
              {detailFor.image_url && <img src={detailFor.image_url} alt={detailFor.title} className="w-full max-h-64 object-cover rounded-lg" />}
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{detailFor.description}</p>
              <div className="space-y-1.5 text-sm">
                {detailFor.price_display && <p className="font-semibold text-[#E87722]">{detailFor.price_display}</p>}
                <p className="text-gray-600">Seller:{' '}
                  {detailFor.seller_id ? (
                    <a href={`/marketplace/seller/${detailFor.seller_id}`} className="font-medium underline hover:text-[#E87722]" data-testid="detail-seller-link">
                      {detailFor.seller_org || detailFor.seller_name}
                    </a>
                  ) : (
                    <span className="font-medium">{detailFor.seller_org || detailFor.seller_name}</span>
                  )}
                  {detailFor.seller_rating && <span className="ml-2"><Stars avg={detailFor.seller_rating.avg} count={detailFor.seller_rating.count} /></span>}
                </p>
                {detailFor.location && <p className="text-gray-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{detailFor.location}</p>}
                {detailFor.contact_phone && <p className="text-gray-500 flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{detailFor.contact_phone}</p>}
              </div>
              <Button className="w-full bg-[#E87722] hover:bg-[#d66a1a]"
                onClick={() => { setDetailFor(null); setEnquireFor(detailFor); }} data-testid="detail-enquire-btn">
                <Send className="h-4 w-4 mr-2" />Enquire
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Rate deal dialog */}
      <Dialog open={!!rateFor} onOpenChange={(o) => !o && setRateFor(null)}>
        <DialogContent data-testid="rate-deal-dialog">
          <DialogHeader>
            <DialogTitle>Rate the seller — {rateFor?.listing_title}</DialogTitle>
            <DialogDescription>How was your experience with {rateFor?.seller_name}? Your rating helps trusted sellers stand out.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setRateStars(n)}
                  className={`text-3xl transition-transform hover:scale-110 ${n <= rateStars ? 'text-amber-500' : 'text-gray-300'}`}
                  data-testid={`rate-star-${n}`}>★</button>
              ))}
            </div>
            <Textarea rows={3} value={rateComment} onChange={(e) => setRateComment(e.target.value)}
              placeholder="Optional comment about the product/service quality, delivery, communication..." data-testid="rate-comment-input" />
            <Button className="w-full bg-[#E87722] hover:bg-[#d66a1a]" onClick={submitRating}
              disabled={busy || rateStars < 1} data-testid="rate-submit-btn">
              {busy ? 'Submitting...' : 'Submit Rating'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enquire dialog */}
      <Dialog open={!!enquireFor} onOpenChange={(o) => !o && setEnquireFor(null)}>
        <DialogContent data-testid="enquire-dialog">
          <DialogHeader>
            <DialogTitle>Enquire — {enquireFor?.title}</DialogTitle>
            <DialogDescription>Your enquiry goes directly to {enquireFor?.seller_org || enquireFor?.seller_name}, who will contact you to take the deal forward. CSED is notified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Message *</Label>
              <Textarea rows={4} value={enquiryMsg} onChange={(e) => setEnquiryMsg(e.target.value)}
                placeholder="Describe your requirement, quantity, timeline..." data-testid="enquiry-message-input" />
            </div>
            <div>
              <Label>Contact phone (optional)</Label>
              <Input value={enquiryPhone} onChange={(e) => setEnquiryPhone(e.target.value)} placeholder="+91..." data-testid="enquiry-phone-input" />
            </div>
            <Button className="w-full bg-[#E87722] hover:bg-[#d66a1a]" onClick={sendEnquiry} disabled={busy || enquiryMsg.trim().length < 10} data-testid="enquiry-submit-btn">
              {busy ? 'Sending...' : 'Send Enquiry'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Post listing dialog */}
      <Dialog open={postOpen} onOpenChange={setPostOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="post-listing-dialog">
          <DialogHeader>
            <DialogTitle>Post a Listing</DialogTitle>
            <DialogDescription>Your listing goes live after CSED admin approval.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex rounded-lg border overflow-hidden w-fit">
              {[['product', 'Product'], ['service', 'Professional Service']].map(([v, label]) => (
                <button key={v} onClick={() => setForm(prev => ({ ...prev, type: v, category: '' }))}
                  className={`px-4 py-2 text-sm font-medium ${form.type === v ? 'bg-[#1E3A5F] text-white' : 'bg-white text-gray-700'}`}
                  data-testid={`listing-type-${v}`}>
                  {label}
                </button>
              ))}
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder={form.type === 'product' ? 'e.g. Organic Cold-Pressed Coconut Oil' : 'e.g. GST & Statutory Audit Services'} data-testid="listing-title-input" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm(prev => ({ ...prev, category: v }))}>
                <SelectTrigger data-testid="listing-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {(form.type === 'product' ? categories.product : categories.service).map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description * (min 20 chars)</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What are you offering, specifications, capacity, experience..." data-testid="listing-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Price (display text)</Label>
                <Input value={form.price_display} onChange={(e) => setForm(prev => ({ ...prev, price_display: e.target.value }))}
                  placeholder="₹500/unit or On request" data-testid="listing-price-input" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="City, State" data-testid="listing-location-input" />
              </div>
            </div>
            <div>
              <Label>Contact phone</Label>
              <Input value={form.contact_phone} onChange={(e) => setForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                placeholder="+91..." data-testid="listing-phone-input" />
            </div>
            <div>
              <Label>Photo (optional, JPG/PNG, max 2MB)</Label>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                  <ImagePlus className="h-4 w-4 text-[#E87722]" />
                  {uploading ? 'Uploading...' : 'Choose image'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                    onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} data-testid="listing-image-input" />
                </label>
                {form.image_url && <img src={form.image_url} alt="preview" className="h-12 w-12 rounded object-cover border" />}
              </div>
            </div>
            <Button className="w-full bg-[#E87722] hover:bg-[#d66a1a]"
              onClick={submitListing}
              disabled={busy || uploading || !form.title.trim() || !form.category || form.description.trim().length < 20}
              data-testid="listing-submit-btn">
              {busy ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
